import React, { useEffect, useState } from "react";
import "highlight.js/styles/github-dark.css";
import hljs from "highlight.js";
import { Check, Copy } from "lucide-react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Button } from "../../ui/button";
import { cn } from "../../../lib/utils";

interface CodeBlockItem {
  key: string;
  label: string;
  children: string;
  language: string;
}

interface CodeBlockProps {
  items: CodeBlockItem[];
  className?: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ items, className }) => {
  const [copied, setCopied] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(items[0]?.key ?? "1");
  
  const activeItem = items.find(item => item.key === activeTab) || items[0];

  useEffect(() => {
    hljs.highlightAll();
  }, [activeTab]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!items || items.length === 0) return null;

  return (
    <div className={cn("relative border border-border rounded-lg overflow-hidden", className)}>
      {/* Tab Headers */}
      {items.length > 1 && (
        <div className="flex bg-muted border-b border-border">
          {items.map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveTab(item.key)}
              className={cn(
                "px-4 py-2 text-sm font-gc-med transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset",
                activeTab === item.key
                  ? "bg-background text-foreground border-b-2 border-maitai-green"
                  : "text-muted-foreground"
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* Copy Button */}
      <div className="absolute top-2 right-2 z-10">
        <CopyToClipboard text={activeItem.children} onCopy={() => setCopied(true)}>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-6 w-6 p-0 bg-background/80 backdrop-blur-sm",
              "hover:bg-accent text-muted-foreground hover:text-foreground",
              "border border-border"
            )}
            aria-label={copied ? "Copied!" : "Copy code"}
          >
            {copied ? (
              <Check className="h-3 w-3" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        </CopyToClipboard>
      </div>

      {/* Code Content */}
      <div className="bg-card">
        <pre className="p-4 overflow-x-auto text-sm">
          <code className={`language-${activeItem.language} hljs`}>
            {activeItem.children}
          </code>
        </pre>
      </div>
    </div>
  );
};

export default CodeBlock;
